/**
 * WRICEF catalog types — mirrors the backend response shape from
 * /api/projects/{id}/wricef-catalog.
 */
import type { components } from "@/types/api.generated";

export type WricefLetter = "W" | "R" | "I" | "C" | "E" | "F";

/**
 * A decisão do arquiteto, no tipo gerado da F1 (#1343). A lista do catálogo
 * não declara modelo no backend, então o item é escrito à mão, mas a decisão
 * vem do contrato.
 */
type WricefDecisionSchema = components["schemas"]["WricefDecisionItem"];

/** As quatro decisões, mais `_UNDECIDED`, o balde do card do Ato 1 (#1500). */
export type WricefCatalogDecisionFilter = NonNullable<WricefDecisionSchema["decision"]> | "_UNDECIDED";

export const WRICEF_NAMES: Record<WricefLetter, string> = {
  W: "Workflow",
  R: "Report",
  I: "Interface",
  C: "Conversion",
  E: "Enhancement",
  F: "Form",
};

export const WRICEF_COLORS: Record<WricefLetter, string> = {
  W: "bg-purple-100 text-purple-800 border-purple-200",
  R: "bg-blue-100 text-blue-800 border-blue-200",
  I: "bg-red-100 text-red-800 border-red-200",
  C: "bg-amber-100 text-amber-800 border-amber-200",
  E: "bg-emerald-100 text-emerald-800 border-emerald-200",
  F: "bg-gray-100 text-gray-800 border-gray-200",
};

export interface CriticalityFactors {
  uso?: number;
  complexity?: number;
  io_density?: number;
  modulo?: number;
  wricef_weight?: number;
}

/** Um sinal de fim-de-vida (EOL) detectado pela análise contextual. */
export interface EolSignal {
  item: string;
  razao: string;
}

/** Detalhes técnicos coletados pela análise (lista de tabelas, FMs, etc.). */
export interface AnalysisDetails {
  tabelas_standard?: string[];
  tabelas_custom?: string[];
  fms_chamados?: string[];
  transacoes_alvo?: string[];
  observacoes?: string;
  [key: string]: unknown;
}

/** Tipo da alternativa de conversão proposta pela análise. */
export type ConversionTipo =
  | "rap_extension"
  | "refactor_obrigatorio"
  | "abandonar"
  | "key_user_extension"
  | "side_by_side_btp"
  | "sem_alteracao"
  | string;

export const CONVERSION_LABELS: Record<string, string> = {
  rap_extension: "RAP Extension",
  refactor_obrigatorio: "Mandatory refactor",
  abandonar: "Abandon / replace",
  key_user_extension: "Key User Extension",
  side_by_side_btp: "Side-by-side (BTP)",
  sem_alteracao: "No change",
  // Crus até a F2b (#1343): o frontend não tinha rótulo para os três.
  standard_equivalent: "Standard equivalent",
  via_parceiro_fiscal: "Via tax partner",
  indefinido: "Undefined",
};

/** Cor do badge por tipo de conversão — vermelho = mais esforço/risco. */
export const CONVERSION_COLORS: Record<string, string> = {
  rap_extension: "bg-amber-100 text-amber-800 border-amber-200",
  refactor_obrigatorio: "bg-red-100 text-red-800 border-red-200",
  abandonar: "bg-rose-100 text-rose-800 border-rose-200",
  key_user_extension: "bg-blue-100 text-blue-800 border-blue-200",
  side_by_side_btp: "bg-purple-100 text-purple-800 border-purple-200",
  sem_alteracao: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

// Grau clean-core oficial (matriz C7/C8). A = compliant/cloud-ready.
export type CleanCoreLevel = "A" | "B" | "C" | "D";

// Modelo de extensibilidade recomendado no S/4 (migration 0025).
// key_user = in-app sem código; side_by_side = BTP; developer = ABAP Cloud (RAP).
export type ExtensibilityTarget = "key_user" | "side_by_side" | "developer";

export interface WricefCatalogItem {
  id: string;
  project_id: string;
  obj_name: string;
  obj_type_tadir: string;
  wricef: WricefLetter;
  wricef_fallback: boolean;
  devclass: string | null;
  module: string | null;
  author: string | null;
  created_on: string | null;
  description_pt: string | null;
  criticality_score: number | null;
  criticality_factors: CriticalityFactors | null;
  source: "zip" | "tadir" | "both" | null;
  code_storage_path: string | null;
  created_at: string;
  updated_at: string;
  // Diagnóstico contextual rico (migration 0015)
  wricef_evidence: string | null;
  sub_lob: string | null;
  scope_item_candidato: string | null;
  conversion_tipo: ConversionTipo | null;
  conversion_descricao: string | null;
  conversion_esforco_h: number | null;
  analysis_confidence: number | null;
  eol_signals: EolSignal[] | null;
  analysis_details: AnalysisDetails | null;
  // Grau clean-core oficial A/B/C/D (migration 0024, consumo C7/C8)
  clean_core_level: CleanCoreLevel | null;
  // Fatos de classificação (migration 0025) — alimentam o effort/custo e a
  // estratégia de extensão. A API já os retorna (wricef_catalog.py:116-117).
  disposition: string | null;
  extensibility_target: ExtensibilityTarget | null;
  // Varredura que catalogou a linha (migration 0026). null = só consolidado.
  scan_job_id: string | null;
  // O módulo que a coluna "Module" mostra (#922): o `stream` do próprio objeto,
  // que vem do APPL do TRDIR, senão o do pacote unânime (#906). É a mesma regra
  // do card "Customizações por módulo" do Diagnóstico. `module` acima é o campo
  // do ingest, e a passada leve o estampa como `CROSS` em tudo.
  resolved_stream: string | null;
  /** O **código** por trás do `resolved_stream` (#906). É ele que volta no
   *  filtro: o texto muda com o idioma, o código não. */
  resolved_appl: string | null;
  /** `true` quando o módulo veio do pacote, e não do próprio objeto. */
  stream_inherited: boolean;
  /** O papel do objeto na unidade de entrega (#1073). `null` em varredura
   *  anterior à agregação. */
  unit_role?: UnitRole | null;
  /** Quem cita a peça no fonte. Vazio para executável, gerado e órfão. */
  unit_owners?: string[];
  /** O fonte foi lido e guardado (#1083). */
  source_read?: boolean | null;
  /** O tipo tem fonte para ler, pelo Guia. Com `source_read` falso, separa a
   *  leitura que falhou (formulário Adobe) do objeto que é assim por natureza
   *  (web service). */
  type_has_code?: boolean | null;
  /** Conteúdo de demonstração da SAP (autor SAP), que só entra no catálogo
   *  pelos pacotes que a conexão declarou (#1072). */
  demo_content?: boolean;
  // A decisão do arquiteto e os valores que valem (#1500, F3), da view
  // `wricef_decision_effective`. Opcionais: a worklist não os traz, e os
  // fixtures dos testes que já existiam não precisam deles. `null` em objeto
  // sem decisão; os dois `_adjusted` dizem se o arquiteto mexeu no campo.
  decision?: WricefDecisionSchema["decision"];
  /** O critério da decisão. */
  decision_note?: string | null;
  decision_standard_scope_item?: string | null;
  decision_strategy_suggested?: string | null;
  /** A estratégia que vale: o ajuste do arquiteto, senão a sugestão da IA. */
  decision_strategy?: string | null;
  decision_strategy_adjusted?: boolean | null;
  /** As horas que valem; 0 em fora do escopo e standard. */
  decision_hours?: number | null;
  decision_hours_adjusted?: boolean | null;
  decision_functional_description?: string | null;
  decision_is_manual_adjustment?: boolean | null;
}

/** Papéis da unidade de entrega (#1073). `ancora` é o executável. */
export type UnitRole = "ancora" | "peca" | "biblioteca" | "orfao" | "configuracao";

/** Uma peça dentro do executável, como `/components` devolve (#1083). A lista
 *  vem em ordem de árvore: o filho logo depois do pai. */
export interface WricefComponent {
  obj_name: string;
  obj_type_tadir: string;
  wricef: WricefLetter | null;
  unit_role: UnitRole | null;
  description_pt: string | null;
  /** De quem esta peça é componente: o executável, ou outra peça. */
  via: string;
  /** 1 é usada direto pelo executável; 2 é peça de uma peça. */
  depth: number;
  source_read: boolean;
  type_has_code: boolean;
}

export interface WricefComponentsResponse {
  obj_name: string;
  components: WricefComponent[];
}

export interface WricefCatalogResponse {
  total: number;
  limit: number;
  offset: number;
  items: WricefCatalogItem[];
  /**
   * Quantos objetos de cada decisão o filtro tem, sem o próprio filtro de
   * decisão (#1500): é o número de cada botão. Chaves da tela, com
   * `_UNDECIDED` no lugar do `undecided` da API (`api/wricef-catalog.ts`).
   */
  decision_counts?: Record<string, number>;
  /**
   * Quantos "Fora do escopo" ainda estão sem motivo, com os mesmos filtros
   * (EF-FUNC-011). Fica fora de `decision_counts`, que a seleção soma: ele já
   * está dentro do `out_of_scope`.
   */
  out_of_scope_without_reason?: number;
}

export interface WricefCatalogSummary {
  /** ⚠️ Conta só linhas com `criticality_score` não-nulo (semântica original,
   *  preservada de propósito). O total verdadeiro do catálogo é `total_rows`. */
  total: number;
  /** A letra, mais os dois baldes que a API manda desde a #920 e que este tipo
   *  não declarava: `_NOT_APPLICABLE` (o Guia diz que o tipo não recebe letra)
   *  e `_UNKNOWN` (nenhuma regra decidiu). São coisas diferentes, e a tela os
   *  trata de formas diferentes — o primeiro é nota, o segundo é badge. */
  by_wricef: Partial<Record<WricefBucket, number>>;
  by_module: Record<string, number>;
  criticality: {
    min: number | null;
    max: number | null;
    avg: number | null;
    p50: number | null;
    p90: number | null;
  };
  // Agregados aditivos que o endpoint devolve desde a DIAG.1 e que este tipo
  // não declarava. O `_UNKNOWN` de cada mapa é balde legítimo: é a coluna nula
  // no dado, e descartá-lo faz o agregado deixar de fechar com o total.
  total_rows?: number;
  by_wricef_disposition?: Record<string, Record<string, number>>;
  by_disposition?: Record<string, number>;
  /** Nível de clean core. O `_UNKNOWN` são os catalogados sem análise (#829). */
  by_clean_core_level?: Record<string, number>;
  /** Cruzamento disposição × alvo de extensibilidade (#687). */
  by_disposition_target?: Record<string, Record<string, number>>;
  by_extensibility_target?: Record<string, number>;
  by_scope_status?: Record<string, number>;
  top_critical?: unknown[];
  /** O eixo da coluna "Module", com a herança por pacote. A chave é o **código**
   *  do componente de aplicação (`APPL`), e `_UNKNOWN` é o balde declarado de
   *  quem não tem módulo. */
  by_stream?: Record<string, number>;
  /** `{código: texto}` no idioma de quem leu, vindo da `TAPLT` do SAP (#906).
   *
   *  Viaja ao lado da contagem, e não como chave dela: a contagem é a mesma em
   *  qualquer idioma, e com o texto como chave o filtro que a tela manda de
   *  volta deixaria de casar ao trocar de idioma, devolvendo lista vazia sem
   *  erro nenhum. O balde `_UNKNOWN` não aparece aqui — a frase dele é da
   *  tela, não da SAP. */
  stream_labels?: Record<string, string>;
  /** Criticidade >= 0,7 no catálogo inteiro (ou no snapshot). */
  high_criticality?: number;
  /** Letra em fallback por tipo TADIR sem mapa, ou descrição que o modelo não
   *  conseguiu escrever. Descrição ausente não conta: a passada leve nunca a gera. */
  needs_review?: number;
  // O que a aba Overview do Architect lê (#922). Ela agregava as 1.000 primeiras
  // linhas no navegador; estes saem do banco, sobre o catálogo inteiro.
  /** Cruzamento módulo resolvido × nível de clean core. */
  by_stream_clean_core_level?: Record<string, Record<string, number>>;
  /** Quantos objetos do catálogo são conteúdo de demonstração da SAP (#1072). */
  demo_content?: number;
  /** Quantos objetos em cada papel da unidade de entrega (#1073). Vazio em
   *  catálogo anterior à agregação. */
  by_unit_role?: Partial<Record<UnitRole, number>>;
  /** Executáveis mais bibliotecas, cada uma contada uma vez. `null` quando o
   *  catálogo não foi agregado. */
  delivery_units?: number | null;
  /** Tipo de conversão; `_UNKNOWN` é o objeto sem tipo (toda a passada leve). */
  by_conversion_type?: Record<string, number>;
  /** Horas de conversão por nível, para separar o avaliado do não avaliado. */
  effort_h_by_clean_core_level?: Record<string, number>;
}

export interface WricefPackageSummary {
  devclass: string;
  total: number;
  analyzed: number;
  /** A letra, mais os dois baldes que a API manda desde a #920 e que este tipo
   *  não declarava: `_NOT_APPLICABLE` (o Guia diz que o tipo não recebe letra)
   *  e `_UNKNOWN` (nenhuma regra decidiu). São coisas diferentes, e a tela os
   *  trata de formas diferentes — o primeiro é nota, o segundo é badge. */
  by_wricef: Partial<Record<WricefBucket, number>>;
  avg_criticality: number | null;
  total_effort_h: number;
}

export interface WricefByPackageResponse {
  packages: WricefPackageSummary[];
  total_packages: number;
}

export interface WricefCatalogQuery {
  /**
   * Estado do tipo pelo Guia de objetos (#1083): `entregavel`, `peca`,
   * `configuracao`, `seguranca`, `excluido`. `entregavel` traz junto o tipo
   * que o Guia ainda não conhece, para que nada suma da tela.
   */
  object_state?: string[];
  /** Papel na unidade de entrega; `configuracao` é o objeto gerado pelo SAP. */
  unit_role?: UnitRole[];
  /** Só a peça que nenhum executável alcança, subindo pelos donos (#1083). */
  without_executable?: boolean;
  wricef?: WricefLetter[];
  module?: string[];
  /** Filtra pelo módulo resolvido (`resolved_stream`); `_UNKNOWN` = sem módulo. */
  stream?: string[];
  min_criticality?: number;
  max_criticality?: number;
  source?: "zip" | "tadir" | "both";
  /** Filtra ao snapshot de uma varredura (wricef_scan_job.id). */
  scan_job_id?: string;
  /**
   * A decisão do arquiteto (#1500), com os tokens da tela. Qualquer valor
   * deixa de fora o objeto sem letra, que não tem decisão.
   */
  decision?: WricefCatalogDecisionFilter[];
  /** Só o "Fora do escopo" sem motivo (EF-FUNC-011), o ponto de partida do lote só com o motivo. */
  out_of_scope_without_reason?: boolean;
  order_by?:
    | "obj_name"
    | "wricef"
    | "criticality_score"
    | "created_on"
    | "updated_at"
    | "devclass"
    | "module"
    | "conversion_esforco_h"
    | "analysis_confidence"
    | "clean_core_level"
    | "stream"
    | "decision";
  order_desc?: boolean;
  limit?: number;
  offset?: number;
}

/** Balde de quem não tem módulo, o mesmo sentinela do agregado do backend. */
export const UNKNOWN_STREAM = "_UNKNOWN";

/** As chaves do `by_wricef`: as seis letras mais os dois baldes declarados. */
export type WricefBucket = WricefLetter | "_NOT_APPLICABLE" | "_UNKNOWN";

/** Tipo que o Guia RICEFW marca N/A: parte de um entregável, não um.
 *
 *  Diferente de `_UNKNOWN` no `by_wricef`, que é "nenhuma regra decidiu". Um é
 *  resposta, o outro é dívida, e a tela os trata de formas diferentes. */
export const NOT_APPLICABLE_BUCKET = "_NOT_APPLICABLE";

export interface WricefWorklistGroup {
  /** Módulo resolvido, ou `UNKNOWN_STREAM`. */
  stream: string;
  count: number;
  /** Quantos do grupo são C/D (o resto entrou por criticidade alta). */
  remediation: number;
}

/** A worklist da aba Decisions & Remediation, paginada no banco. */
export interface WricefWorklistResponse {
  /** Total do filtro. */
  total: number;
  limit: number;
  offset: number;
  /** Todos os módulos da worklist, na ordem da tela, mesmo com filtro ligado.
   *  A chave é o código do componente de aplicação (#906). */
  groups: WricefWorklistGroup[];
  /** `{código: texto}` no idioma de quem leu, da `TAPLT` do SAP. */
  stream_labels?: Record<string, string>;
  items: WricefCatalogItem[];
}

export interface WricefWorklistQuery {
  stream?: string[];
  scan_job_id?: string;
  limit?: number;
  offset?: number;
}
